import type { InvoiceDocumentTitleValue, PaymentMethod } from "@stb/api-contract"
import { formatMoney, money } from "@stb/core/money"

import type { InvoiceLook } from "./look"

/**
 * Pure document data for an invoice (invoicing plan, "PDF and email"). The page preview, the
 * PDF HTML and the email text are all built from `buildInvoiceDocument`, so they cannot drift.
 * No server imports: the UI imports this file directly.
 *
 * A seller that is not GST registered gets a plain "Invoice": no GST column, no GST row and no
 * "Tax Invoice" words anywhere in the data (a test scans the whole object).
 */

/** What an invoice shows as the seller's name until the business name is set (preview only: Send needs a name). */
export const SELLER_NAME_PLACEHOLDER = "Your business"

export interface InvoiceDocumentInput {
  seller: {
    businessName: string
    abn: string | null
    gstRegistered: boolean
    addressLines: readonly string[]
    bank: { accountName: string | null; bsb: string | null; accountNumber: string | null }
  }
  buyer: { name: string | null; abn: string | null; address: string | null }
  /** `null` while the invoice is still a draft. */
  number: string | null
  documentTitle: InvoiceDocumentTitleValue
  issueDate: string
  dueDate: string
  amountsAre: "inclusive" | "exclusive"
  note: string | null
  showBankDetails: boolean
  lines: ReadonlyArray<{
    description: string
    quantityMilli: number
    unitAmountMinor: number
    taxCode: "GST" | "GST_FREE"
    netMinor: number
    gstMinor: number
    grossMinor: number
  }>
  subtotalMinor: number
  gstMinor: number
  totalMinor: number
  roundingAdjustmentMinor: number
  /** Frozen at Send; absent = Classic without brand. */
  look?: InvoiceLook | null
  /** Set when the invoice is paid in full: the PAID stamp, date and method (T476). */
  paid?: { receivedOn: string; method: PaymentMethod } | null
}

const METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_transfer: "Bank transfer",
  cash: "Cash",
  card: "Card",
  other: "Other",
}

export interface InvoiceDocumentData {
  title: "Tax Invoice" | "Invoice"
  number: string
  showGst: boolean
  seller: { name: string; abn: string | null; addressLines: string[] }
  buyer: { name: string | null; abn: string | null; address: string | null }
  issueDate: string
  dueDate: string
  lines: Array<{
    description: string
    quantity: string
    unitAmount: string
    /** Present only when `showGst`. */
    gst?: string
    /** Per-line tax status; present only when `showGst`. */
    taxLabel?: "GST" | "GST-free"
    amount: string
  }>
  totals: Array<{ label: string; amount: string }>
  bank: { accountName: string | null; bsb: string | null; accountNumber: string | null } | null
  note: string | null
  /** Presentation only (template, colours, logo); never hides or adds a required field. */
  look?: InvoiceLook
  /** Present only for an invoice paid in full. */
  paid?: { receivedOn: string; method: string }
}

const LOCALE = "en-AU"
const aud = (minor: number) => formatMoney(money(minor, "AUD"), LOCALE)

/** `1500` thousandths -> `"1.5"`; whole quantities have no decimals. */
function formatQuantity(milli: number): string {
  const whole = Math.trunc(milli / 1000)
  const fraction = String(Math.abs(milli % 1000))
    .padStart(3, "0")
    .replace(/0+$/, "")
  return fraction ? `${whole}.${fraction}` : String(whole)
}

/** `51824753556` -> `51 824 753 556`. */
export function formatAbn(abn: string): string {
  const digits = abn.replace(/\s+/g, "")
  return digits.length === 11
    ? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
    : abn
}

export function buildInvoiceDocument(input: InvoiceDocumentInput): InvoiceDocumentData {
  const showGst = input.seller.gstRegistered
  const taxInvoice = showGst && input.documentTitle === "tax_invoice"
  const totals: InvoiceDocumentData["totals"] = []
  if (showGst) {
    totals.push({ label: "Subtotal", amount: aud(input.subtotalMinor) })
    totals.push({ label: "GST", amount: aud(input.gstMinor) })
  }
  totals.push({ label: "Total", amount: aud(input.totalMinor) })

  return {
    title: taxInvoice ? "Tax Invoice" : "Invoice",
    number: input.number ?? "Draft",
    showGst,
    seller: {
      name: input.seller.businessName,
      abn: input.seller.abn,
      addressLines: [...input.seller.addressLines],
    },
    buyer: input.buyer,
    issueDate: input.issueDate,
    dueDate: input.dueDate,
    lines: input.lines.map((line) => ({
      description: line.description,
      quantity: formatQuantity(line.quantityMilli),
      unitAmount: aud(line.unitAmountMinor),
      ...(showGst
        ? { gst: aud(line.gstMinor), taxLabel: line.taxCode === "GST" ? "GST" : "GST-free" }
        : {}),
      amount: aud(showGst && input.amountsAre === "exclusive" ? line.netMinor : line.grossMinor),
    })),
    totals,
    bank: input.showBankDetails ? input.seller.bank : null,
    note: input.note,
    ...(input.look ? { look: input.look } : {}),
    ...(input.paid
      ? { paid: { receivedOn: input.paid.receivedOn, method: METHOD_LABELS[input.paid.method] } }
      : {}),
  }
}

/** Plain-text rendering for the email body and tests. */
export function renderInvoiceText(data: InvoiceDocumentData): string {
  const out: string[] = []
  out.push(`${data.title} ${data.number}`)
  out.push(data.seller.name)
  if (data.seller.abn) out.push(`ABN ${formatAbn(data.seller.abn)}`)
  out.push(...data.seller.addressLines)
  out.push("")
  out.push(`Issued: ${data.issueDate}`)
  out.push(`Due: ${data.dueDate}`)
  if (data.buyer.name || data.buyer.abn) {
    out.push("")
    out.push("Bill to")
    if (data.buyer.name) out.push(data.buyer.name)
    if (data.buyer.abn) out.push(`ABN ${formatAbn(data.buyer.abn)}`)
    if (data.buyer.address) out.push(data.buyer.address)
  }
  out.push("")
  for (const line of data.lines) {
    const gst = line.gst === undefined ? "" : ` (${line.taxLabel} ${line.gst})`
    out.push(`${line.description}  ${line.quantity} x ${line.unitAmount}${gst}  ${line.amount}`)
  }
  out.push("")
  for (const row of data.totals) out.push(`${row.label}: ${row.amount}`)
  if (data.paid) out.push(`PAID ${data.paid.receivedOn} by ${data.paid.method}`)
  if (data.bank) {
    out.push("")
    out.push("Pay by bank transfer")
    if (data.bank.accountName) out.push(`Account name: ${data.bank.accountName}`)
    if (data.bank.bsb) out.push(`BSB: ${data.bank.bsb}`)
    if (data.bank.accountNumber) out.push(`Account number: ${data.bank.accountNumber}`)
  }
  if (data.note) {
    out.push("")
    out.push(data.note)
  }
  return out.join("\n")
}
